import { defineStore } from 'pinia'
import { ref, watch } from 'vue'
import { useRouter } from 'vue-router'
import { useRemoteImportStore } from '@/stores/remote-import'
import { http } from '@/helpers/http'
import { track } from '@/helpers/mixpanelDes'
import type { Node } from '@tiptap/pm/model'
import type { TTranscriptionLegacyEvent } from '@/types/remote-imports'
import type {
  IHighlight,
  TFrame,
  TMonologue,
  TMonologueElement,
  TMonologues,
  TMonologueTextElement,
  TTranscription,
  TWord,
} from '@/types/transcription'
import type { TDraft, TDraftType } from '@/types/drafts'
import type { JSONContent } from '@tiptap/vue-3'
import type { Editor } from '@tiptap/core'

export const useTranscriptionStore = defineStore('transcription', () => {
  const router = useRouter()
  const remoteImports = useRemoteImportStore()
  const player = ref<HTMLVideoElement | null>(null)
  const frame = ref<number | null>(null)
  const playing = ref<boolean>(false)
  const endTime = ref<number | null>(null)
  const startTime = ref<number | null>(null)

  watch(player, () => {
    if (!player.value) return

    player.value.addEventListener('pause', () => {
      playing.value = false
      endTime.value = null
      startTime.value = null
      player.value && (player.value.ontimeupdate = null)
    })

    player.value.addEventListener('play', () => {
      playing.value = true
    })
  })

  // todo: remove after production release
  function handleNewProjectForLegacyTranscription(data: TTranscriptionLegacyEvent, draftId: number | null): void {
    track('transcription-create-content-legacy')
    remoteImports.setNewProjectData(draftId, data.title, data.content, undefined)
    router.push('/creator/templates')
  }

  function tipTapToContent(content: JSONContent): TTranscription {
    const transcription: TTranscription = {
      timeFrameLength: 0,
      timeFrames: [],
    }

    content.content?.forEach((node) => {
      if (node.type === 'transcriptionFrame') {
        const frame: TFrame = {
          endTime: node.attrs?.endTime,
          startTime: node.attrs?.startTime,
          speakerName: node.attrs?.speakerName,
          speakerNumber: node.attrs?.speakerNumber,
          ...(node.attrs?.note && { note: node.attrs.note }),
          words: [],
        }

        node.content?.forEach((textNode) => {
          if (textNode.type === 'text') {
            const text = textNode.text || ''
            const marks = textNode.marks || []

            let currentWord: TWord | null = null

            marks.forEach((mark) => {
              if (mark.type === 'transcriptionWord') {
                if (currentWord) {
                  frame.words.push(currentWord)
                }

                currentWord = {
                  startTime: mark.attrs?.startTime,
                  endTime: mark.attrs?.endTime,
                  word: text,
                }
              }

              if (mark.type === 'transcriptionMark' && currentWord) {
                currentWord.word = `<mark${mark.attrs?.class === 'blockquote' ? ' class="blockquote"' : ''}>${text}</mark>`
              }
            })

            if (currentWord) {
              frame.words.push(currentWord)
            }
          } else if (textNode.type === 'transcriptionImage') {
            const currentWord: TWord = {
              startTime: textNode.attrs?.startTime,
              endTime: textNode.attrs?.endTime,
              word: `<img src='${textNode.attrs?.src}' class='audio-import-image'>`,
            }

            frame.words.push(currentWord)
          }
        })

        transcription.timeFrames.push(frame)
      }
    })

    if (transcription.timeFrames[0]) {
      const lastFrame = transcription.timeFrames[transcription.timeFrames.length - 1]!
      transcription.timeFrameLength = lastFrame.endTime - transcription.timeFrames[0].startTime
    }

    return transcription
  }

  function isTranscriptionType(type: TDraftType | null | undefined): boolean {
    return type === 'Audio' || type === 'TemiJob'
  }

  function draftToProjectContent(draft: Partial<TDraft> | null, editor: Editor | null = null): string {
    if (!draft?.content) return ''
    if (!isTranscriptionType(draft.type)) return draft.content

    try {
      return contentToProject(JSON.parse(draft.content), editor)
    } catch {
      return contentToHTML(draft.type, draft.content)
    }
  }

  function contentToHTML(type: TDraftType | undefined, content: string): string {
    track('transcription-open', { type })
    if (!content || !type) return '<p></p>'

    return type === 'TemiJob' ? monologues2HTML(content) : timeFrames2HTML(content)
  }

  function monologues2HTML(content: string): string {
    try {
      const transcription: TMonologues = JSON.parse(content)
      const html = transcription.monologues.reduce((prev: string, frame: TMonologue, frameIndex) => {
        const startTime = (frame.elements.filter((e) => e.type === 'text') as TMonologueTextElement[]).shift()?.ts
        const endTime = (frame.elements.filter((e) => e.type === 'text') as TMonologueTextElement[]).pop()?.end_ts
        return `${prev}
          <p
            data-start-time="${startTime}"
            data-end-time="${endTime}"
            data-frame-index="${frameIndex}"
            data-speaker-name="Speaker ${frame.speaker + 1}"
            data-speaker-number="${frame.speaker}"
          >${frame.elements.reduce((prev: string, word: TMonologueElement, wordIndex) => {
            const isImg = word.value.includes('<img ')
            return `${prev}${
              word.type == 'punct'
                ? ''
                : isImg
                  ? word.value.replace(
                      '<img ',
                      `<img
                        data-start-time="${word.ts}"
                        data-end-time="${word.end_ts}"
                        data-word-index="${wordIndex}"
                        data-frame-index="${frameIndex}" `,
                    )
                  : `<span
                      data-start-time="${word.ts}"
                      data-end-time="${word.end_ts}"
                      data-word-index="${wordIndex}"
                      data-frame-index="${frameIndex}"
                    >${word.value}${frame.elements[wordIndex + 1]?.type === 'punct' ? `${frame.elements[wordIndex + 1]!.value} ` : ''}</span>`
            }`
          }, '')}</p>`
      }, '')

      return html
    } catch (e) {
      console.log(e)
      track('transcription-open-temijob')
      return '<p>Invalid import content...</p>'
    }
  }

  function timeFrames2HTML(content: string): string {
    try {
      const transcription: TTranscription = JSON.parse(content)
      const html = transcription.timeFrames.reduce((prev: string, frame: TFrame, frameIndex) => {
        return (
          `${prev}<p ` +
          `data-start-time="${frame.startTime}" ` +
          `data-end-time="${frame.endTime}" ` +
          `data-frame-index="${frameIndex}" ` +
          (frame.note ? `data-note="${frame.note}" ` : '') +
          `data-speaker-name="${frame.speakerName}" ` +
          `data-speaker-number="${frame.speakerNumber}">${frame.words.reduce((prev: string, word: TWord, wordIndex) => {
            const isImg = word.word.includes('<img ')
            return `${prev}${
              isImg
                ? word.word.replace(
                    '<img ',
                    '<img ' +
                      `data-start-time="${word.startTime}" ` +
                      `data-end-time="${word.endTime}" ` +
                      `data-word-index="${wordIndex}" ` +
                      `data-frame-index="${frameIndex}" `,
                  )
                : '<span ' +
                  `data-start-time="${word.startTime}" ` +
                  `data-end-time="${word.endTime}" ` +
                  `data-word-index="${wordIndex}" ` +
                  `data-frame-index="${frameIndex}">${word.word}</span>`
            }`
          }, '')}</p>`
        )
      }, '')

      return html
    } catch {
      track('transcription-open-audio')
      return '<p>Invalid import content...</p>'
    }
  }

  function secondToMinAndSec(seconds: number): string {
    const minutes = Math.floor(seconds / 60)
    const remainingSeconds = Math.floor(seconds % 60)

    return `${minutes}:${remainingSeconds < 10 ? `0${remainingSeconds}` : remainingSeconds}`
  }

  function goStart(): void {
    if (!player.value || !endTime.value || !startTime.value) return

    if (player.value.currentTime >= endTime.value) {
      player.value.currentTime = startTime.value
    }
  }

  function togglePlayLoop(start: number, end: number): void {
    if (!player.value) return

    try {
      endTime.value = end
      startTime.value = start
      player.value.ontimeupdate = () => goStart()
      player.value.currentTime = start
      player.value.play()
    } catch (e) {
      console.log(e)
    }
  }

  function togglePlayTime(time: number): void {
    if (!player.value) return

    try {
      if (player.value.paused) {
        player.value.currentTime = time
        player.value.play()
      } else {
        player.value.pause()
      }
    } catch (e) {
      console.log(e)
    }
  }

  function togglePlayFrame(frameIndex: number, time: number): void {
    if (!player.value) return

    try {
      if (player.value.paused) {
        if (frame.value !== frameIndex) {
          player.value.currentTime = time
          frame.value = frameIndex
        }

        player.value.play()
      } else {
        player.value.pause()
      }
    } catch (e) {
      console.log(e)
    }
  }

  async function grabScreen(currentTime: number): Promise<string | null> {
    if (!player.value) return null

    const canvas = document.createElement('canvas')
    const image = document.createElement('img')
    const id = (Date.now().toString(36) + Math.random().toString(36).slice(2, 7)).toUpperCase()

    player.value.currentTime = currentTime
    canvas.width = player.value.videoWidth
    canvas.height = player.value.videoHeight
    canvas.getContext('2d')?.drawImage(player.value, 0, 0, canvas.width, canvas.height)
    image.src = canvas.toDataURL()

    return await http.post(`/audio/${id}/save-image`, { data: image.src }, true)
  }

  function getHighlights(editor: Editor): IHighlight[] {
    const result: IHighlight[] = []
    let currentGroup: IHighlight | null = null

    editor.state.doc.descendants((frame) => {
      if (frame.type.name === 'transcriptionFrame') {
        frame.descendants((childNode, pos) => {
          const mark = childNode.marks.find(
            (mark) => mark.type.name === 'transcriptionMark' && mark.attrs.class === null,
          )
          const word = childNode.marks.find((mark) => mark.type.name === 'transcriptionWord')
          const isEmpty = childNode.textContent.trim() === '' // keep collecting previous words if empty

          if (mark && word) {
            if (!currentGroup) {
              currentGroup = {
                frame,
                word,
                content: childNode.text ?? '',
                mark,
                pos,
              }
            } else {
              currentGroup.content += childNode.text
            }
          } else if (currentGroup && !isEmpty) {
            result.push(currentGroup)
            currentGroup = null
          }
        })

        if (currentGroup) {
          result.push(currentGroup)
          currentGroup = null
        }
      }
    })

    return result
  }

  function getNotes(editor: Editor): Node[] {
    const content = editor.state.doc.content as any
    return content.content.filter((node: Node) => node.type.name === 'transcriptionFrame' && node.attrs.note)
  }

  function contentToProject(transcription: TTranscription, editor: Editor | null): string {
    track('transcription-create-content')
    const wordToString = (word: string): string =>
      new DOMParser().parseFromString(word, 'text/html').body.textContent || ''
    const notes = editor ? getNotes(editor) : []
    const highlights = editor ? getHighlights(editor) : []
    let html = ''

    if (highlights.length > 0) {
      html += '<h2>Highlights:</h2>'
      highlights.forEach((highlight) => {
        html += `<p><span class="timestamp">${secondToMinAndSec(highlight.word.attrs.startTime)}:<span> ${highlight.content}</p>`
      })
    }

    if (notes.length > 0) {
      html += '<h2>Notes:</h2>'
      notes.forEach((note) => {
        html += `<p><span class="timestamp">${secondToMinAndSec(note.attrs.startTime)}:</span> ${note.attrs.note}</p>`
      })
    }

    if (highlights.length || notes.length) {
      html += '<h2>Transcription:</h2>'
    }

    html += transcription.timeFrames
      .map((frame) => {
        let isMarkCollecting = false
        return `<p><span class="speakername" id="">${frame.speakerName}:</span> ${frame.words
          .map((word, index) => {
            const isMark = word.word.includes('<mark class="blockquote"')
            const isMarkOnNext = frame.words[index + 1]?.word.includes('<mark class="blockquote"')

            if (isMark) {
              let part = ''
              if (!isMarkCollecting) {
                isMarkCollecting = true
                part += '</p><blockquote><p>'
              }

              part += wordToString(word.word)

              if (!isMarkOnNext) {
                isMarkCollecting = false
                part += `</p><footer>${frame.speakerName}<cite title="Source Title">Source Title</cite></footer></blockquote><p>`
              }

              return part
            } else if (word.word.includes('<img ')) {
              return `</p>${word.word}<p>`
            } else {
              return word.word
            }
          })
          .join('')}</p>`.replace(/<\/p><p>/g, '')
      })
      .join('')

    return html
  }

  function generateSRT(content: string): string {
    const transcription: TTranscription = JSON.parse(content)
    const padZero = (n: number, l: number = 2): string => n.toString().padStart(l, '0')
    const formatTime = (seconds: number): string => {
      const hours = Math.floor(seconds / 3600)
      const minutes = Math.floor((seconds % 3600) / 60)
      const secs = Math.floor(seconds % 60)
      const ms = Math.floor((seconds % 1) * 1000)

      return `${padZero(hours)}:${padZero(minutes)}:${padZero(secs)},${padZero(ms, 3)}`
    }

    let srtContent = ''
    let subtitleIndex = 1

    transcription.timeFrames.forEach((timeFrame) => {
      if (timeFrame.words.length === 0) return

      let currentLine: TWord[] = []
      let lineStartTime = parseFloat(timeFrame.words[0]!.startTime)
      let currentWord = ''
      let currentWordStart = '0'

      timeFrame.words.forEach((word, index) => {
        const cleanWord = word.word.replace(/<[^>]*>/g, '')

        if (cleanWord.trim()) {
          if (currentWord === '') {
            currentWordStart = word.startTime
          }
          currentWord += cleanWord
        }

        const isLastWord = index === timeFrame.words.length - 1
        const isEndOfWord = cleanWord.endsWith(' ') || isLastWord

        if (isEndOfWord && currentWord.trim()) {
          currentLine.push({
            startTime: currentWordStart,
            endTime: word.endTime,
            word: currentWord.trim(),
          })
          currentWord = ''

          if (currentLine.length === 10 || isLastWord || currentLine[currentLine.length - 1]!.word.match(/[.!?…]$/)) {
            const lineText = currentLine.map((w) => w.word).join(' ')
            const lineEndTime = parseFloat(currentLine[currentLine.length - 1]!.endTime)

            srtContent += `${subtitleIndex}\n`
            srtContent += `${formatTime(lineStartTime)} --> ${formatTime(lineEndTime)}\n`
            srtContent += `${lineText}\n\n`

            subtitleIndex++
            lineStartTime =
              index < timeFrame.words.length - 1 ? parseFloat(timeFrame.words[index + 1]!.startTime) : lineEndTime
            currentLine = []
          }
        }
      })
    })

    return srtContent.trim()
  }

  return {
    player,
    frame,
    playing,
    grabScreen,
    secondToMinAndSec,
    handleNewProjectForLegacyTranscription,
    isTranscriptionType,
    draftToProjectContent,
    contentToHTML,
    contentToProject,
    togglePlayFrame,
    togglePlayTime,
    togglePlayLoop,
    tipTapToContent,
    getHighlights,
    getNotes,
    generateSRT,
  }
})
